/**
 * Plan policy v2: the words. Every banner, state cell and dialog line about a
 * project's plan state is built here, from the decisions in plan-state.ts.
 *
 * The same file lives in skapi-rebrand. The two copies differ ONLY in the
 * "this dashboard" section below (which pages exist here, the disabled banner,
 * the docs links and the file storage footnote); everything under it is
 * identical, so a wording change is made in both at once.
 *
 * Copy rules: no em or en dashes; deletion dates read "as early as <date>" (the
 * sweep also waits for a final notice and runs every 12 hours); a project with
 * a Stripe subscription is never promised a deletion date.
 */
import { dateFormat } from "@/code/utils";
import {
    convertBytesToReadableStorage,
    limitText,
    modelSpecs,
    planKeyForGroup,
    planSwitches,
    storedLimitsForPlan,
} from "@/code/service-spec";
import {
    STORED_LIMIT_LABEL,
    COMPLAINT_SHUTOFF_COUNT,
    COMPLAINT_SHUTOFF_MIN_SENDS,
    COMPLAINT_SHUTOFF_PERCENT,
    FREE_OWNER_MONTHLY_SENDS,
    QUOTA_SUSPEND_MIN_BYTES,
    bandwidthBlocked,
    bannerIds,
    cancelConsequence,
    firstOfNextUtcMonth,
    hasSubscriptionId,
    nearStoredLimits,
    newsletterSendBlock,
    projectState,
    resumeKind,
    serviceStatusLabel,
    stateCell,
    storedUsage,
    suspendedKind,
    suspendingLimits,
    suspensionClock,
    suspensionLimits,
} from "@/code/plan-state";
import type { BannerId, ResumeKind, StoredLimitKey, SuspensionClock } from "@/code/plan-state";

export type BannerAction = {
    label: string;
    to?: string; // a route in this dashboard
    href?: string; // another origin, opened in a new tab
    run?: "refresh" | "billing";
};

/** One limit line under a banner or in a dialog. `hint` is a plain-text pointer
 *  to where the data can be deleted when there is no page to link to. */
export type LimitRow = { key: StoredLimitKey; text: string; hint?: string; action?: BannerAction };

export type ProjectBanner = {
    id: BannerId;
    tone: "warning" | "danger";
    icon: string; // Material Symbols name, from the index.html icon_names allowlist
    text: string;
    rows: LimitRow[];
    after: string;
    actions: BannerAction[];
};

/** The parts of a Service instance this file reads. */
export type ServiceLike = {
    id: string;
    service: any;
    subscription?: any;
    formattedSpecs?: any;
};

/* ── this dashboard ─────────────────────────────────────────────────────────
 * The only section that differs between www.bunnyquery.com and skapi-rebrand. */

/**
 * The other dashboards, in one place. `projectPath` null means that origin does
 * not serve per-project pages this copy could link to, so the copy names the
 * place in plain text instead of linking to a page that would 404. www.skapi.com
 * still serves /my-services/:service; skapi-rebrand's /my-projects routes exist
 * there only once it replaces www.skapi.com (owner question 7). Production
 * origins in every build mode.
 */
export const CROSS_DASHBOARD: { [name: string]: { label: string; origin: string; projectPath: string | null } } = {
    skapi: { label: "Skapi", origin: "https://www.skapi.com", projectPath: null },
    bunnyquery: { label: "BunnyQuery", origin: "https://www.bunnyquery.com", projectPath: "/my-projects/" },
};

/** Where an over-limit key's data can be deleted, from this dashboard. File
 *  storage points at its largest share: hosting, record files or the AI index.
 *  skapi-rebrand has record, hosting and newsletter pages; the AI index files
 *  are managed in the BunnyQuery dashboard. */
function dataLink(svc: ServiceLike, key: StoredLimitKey): { action?: BannerAction; hint?: string } {
    const id = svc.id;
    const bunnyquery = CROSS_DASHBOARD.bunnyquery;
    if (key === "dbst") return { action: { label: "Delete records", to: `/my-projects/${id}/records` } };
    if (key === "emlst") return { action: { label: "Delete newsletters", to: `/my-projects/${id}/newsletter` } };
    const info = svc.service?.storage_info || {};
    const host = Number(info.host) || 0;
    const cloud = Number(info.cloud) || 0;
    const file = Number(info.file) || 0;
    if (file > host && file > cloud) {
        return bunnyquery.projectPath
            ? { action: { label: `Open ${bunnyquery.label}`, href: `${bunnyquery.origin}${bunnyquery.projectPath}${id}/dbfile` } }
            : { hint: `Delete indexed files in the ${bunnyquery.label} dashboard.` };
    }
    if (cloud > host) return { action: { label: "Delete records", to: `/my-projects/${id}/records` } };
    return { action: { label: "Delete files", to: `/my-projects/${id}/hosting` } };
}

function disabledBanner(svc: ServiceLike): Omit<ProjectBanner, "id"> {
    return {
        tone: "warning",
        icon: "block",
        text: "This project is disabled. Its users cannot use it until you enable it again.",
        rows: [],
        after: "",
        actions: [{ label: "Settings", to: `/my-projects/${svc.id}` }],
    };
}

/** Where the plan rules are published (documentation/introduction/plans.md and
 *  email/newsletters.md). The plan card footnotes and the Plan & Usage meters
 *  link here instead of repeating the rules. Same base as the navBar Docs link. */
const DOCS_ORIGIN = "https://docs.skapi.com";
export const PLAN_DOCS = {
    plans: `${DOCS_ORIGIN}/introduction/plans.html`,
    hardStop: `${DOCS_ORIGIN}/introduction/plans.html#trial-and-standard-hard-stop`,
    bandwidth: `${DOCS_ORIGIN}/introduction/plans.html#bandwidth`,
    emailStorage: `${DOCS_ORIGIN}/introduction/plans.html#email-storage`,
    complaints: `${DOCS_ORIGIN}/introduction/plans.html#newsletter-complaints`,
    sending: `${DOCS_ORIGIN}/email/newsletters.html#sending-limits`,
};

/** The project page's other documentation links (views/service/service.vue and its
 *  cards). The docs have no Email Alias section of their own; Automated Emails opens
 *  with what an alias is for and where to register it. */
export const PROJECT_DOCS = {
    gettingStarted: `${DOCS_ORIGIN}/introduction/getting-started.html`,
    examples: `${DOCS_ORIGIN}/full-example/intro.html`,
    aiAgents: `${DOCS_ORIGIN}/introduction/ai-driven.html`,
    hosting: `${DOCS_ORIGIN}/hosting/hosting.html#registering-your-subdomain`,
    emailAlias: `${DOCS_ORIGIN}/email/email-templates.html`,
};

/** The footnote of a card's File Storage line: what shares the one pooled
 *  limit, in the words the documentation uses. */
const FILE_POOL_NOTE = "File storage is one pooled limit shared by web hosting, record file attachments and AI indexed files.";

/* ── shared from here down ─────────────────────────────────────────────────── */

const FREE_PLAN = "Free";
const STANDARD_PLAN = "Standard";

// Assembled at run time, like the &#64; entities the templates use.
const SUPPORT_EMAIL = ["support", "broadwayinc.com"].join("@");

/** The ceiling above which a plan that includes NO email storage suspends, as
 *  the meters print it. Only a served limit of 0 reaches it, which no plan has
 *  once the v2 Trial limits are live; it stays for the regions still serving 0. */
const EMAIL_SUSPEND_TEXT = convertBytesToReadableStorage(QUOTA_SUSPEND_MIN_BYTES).replace(".00", "");

/**
 * The email storage rule, one sentence per plan (owner decision 11 of
 * 2026-09-17, which gave Free its own email storage and took email storage out
 * of Free's suspension triggers). The footnote of the cards' Email Storage
 * line; the meter under it says the one half that applies to the project.
 */
export const EMAIL_STORAGE_RULE =
    `On Free and Standard, exceeding email storage deletes the oldest newsletters to reclaim space, `
    + `and the project is never suspended for it. On Premium nothing is ever deleted: the excess is billed.`;

/* ── newsletter sending ─────────────────────────────────────────────────────
 * The three rules of owner decision 12 (2026-09-17), written once and shown in
 * the plan card footnotes and on the newsletter page. The
 * NUMBER of sends a plan includes is never quoted here: that is a plan limit and
 * comes from GET /plan. */

/** How a send is counted. Per RECIPIENT, which is the part owners get wrong: a
 *  list at the plan's subscriber ceiling spends a whole allowance in one send. */
export const SEND_COUNTING_RULE =
    "Newsletter sends are counted per recipient, so one newsletter to a full subscriber list uses "
    + "one send for every subscriber on it.";

/** The per-owner monthly cap that free projects share (decision 12b). Standard
 *  and Premium are always counted per project and have no owner cap. */
export const FREE_OWNER_CAP_RULE =
    `On top of each free project's own monthly sends, one owner can send ${FREE_OWNER_MONTHLY_SENDS} `
    + `newsletter mails a month in total across every free project they have.`;

/** The complaint shutoff, on every plan (decision 12c and 12d). */
export const COMPLAINT_SHUTOFF_RULE =
    `A project stops sending newsletters when it reaches ${COMPLAINT_SHUTOFF_COUNT} spam complaints in a month, `
    + `or a complaint rate of ${COMPLAINT_SHUTOFF_PERCENT} percent once ${COMPLAINT_SHUTOFF_MIN_SENDS} mails have gone out. `
    + `Only newsletter sending stops: the project keeps working, nothing is suspended, and Skapi staff clear the block after a look.`;

/** What a refused newsletter gets back (decision 12e). */
export const SEND_REFUSED_REPLY_RULE =
    "A newsletter that is refused for any of these gets a reply from the project's newsletter address saying which limit was reached.";

/** A whole count as the meters print it ("1,200"). */
const groupCount = (n: unknown): string => {
    const v = Number(n);
    return Number.isFinite(v) ? Math.max(0, Math.trunc(v)).toLocaleString("en-US") : "0";
};

/** The complaint shutoff in the short form a footnote carries. */
const COMPLAINT_SHUTOFF_NOTE =
    `On every plan, newsletter sending stops at ${COMPLAINT_SHUTOFF_COUNT} spam complaints a month, `
    + `or a ${COMPLAINT_SHUTOFF_PERCENT} percent complaint rate once ${COMPLAINT_SHUTOFF_MIN_SENDS} mails have gone out, `
    + `until Skapi staff clear it.`;

/** What limits this project's sending, in one paragraph: how a send is counted,
 *  the per-owner cap where it applies, and the complaint shutoff. The owner cap
 *  is named only on a free project, so a paid one is not told about a rule it
 *  does not have. */
export function newsletterSendRules(svc?: ServiceLike | null): string {
    const parts = [SEND_COUNTING_RULE];
    if (planKeyOf(svc as ServiceLike) === FREE_PLAN) parts.push(FREE_OWNER_CAP_RULE);
    parts.push(COMPLAINT_SHUTOFF_RULE);
    return parts.join(" ");
}

/** The same rules in one line, for the Monthly sends row of the Newsletters page. The plan
 *  cards keep the full wording above; the page only needs the reminder. */
export function newsletterSendHint(svc?: ServiceLike | null): string {
    const parts = ["Counted per recipient."];
    if (planKeyOf(svc as ServiceLike) === FREE_PLAN) parts.push(`Free projects share ${FREE_OWNER_MONTHLY_SENDS} sends a month per owner.`);
    parts.push(`Sending stops at ${COMPLAINT_SHUTOFF_COUNT} spam complaints or a ${COMPLAINT_SHUTOFF_PERCENT}% complaint rate.`);
    return parts.join(" ");
}

/* ── plan card footnotes ────────────────────────────────────────────────────
 * The cards print headline figures only (service-spec.ts derivePlan). A line
 * that needs qualifying carries a note id, and the upgrade page prints these
 * notes under the cards with a numbered marker on the line. Every rule the
 * cards used to spell out is in one of them, with a link to its docs section,
 * except the overage rates, which stay on the Premium card itself. */

export type PlanNoteId = "pool" | "emlst" | "egres" | "emlsd" | "stop";
export type PlanNote = { id: PlanNoteId; text: string; link?: { label: string; href: string } };

/** Card order: the notes are numbered in the order their lines appear. */
const PLAN_NOTE_ORDER: PlanNoteId[] = ["pool", "emlst", "egres", "emlsd", "stop"];

/** ["Free", "Standard"] -> "Free and Standard". */
function joinLabels(labels: string[]): string {
    if (labels.length < 2) return labels[0] || "";
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * The footnotes for the cards of these plans, in marker order. Only a note that
 * some shown card's line points at is returned, so the numbers never skip, and
 * a rule is written for the plans actually on screen (the per-owner cap only
 * while the Free card is shown).
 */
export function planCardNotes(planKeys: string[]): PlanNote[] {
    const plans = planKeys.map((key) => ({ key, spec: modelSpecs[key] })).filter((p) => p.spec);
    const used = new Set<string>();
    for (const p of plans) for (const line of p.spec.offer_list || []) if (line.note) used.add(line.note);

    const stopping = plans.filter((p) => p.spec.hard_stop !== false);
    const billing = plans.filter((p) => p.spec.hard_stop === false);
    const labels = (list: typeof plans) => joinLabels(list.map((p) => p.spec.label));

    const build = (id: PlanNoteId): PlanNote | null => {
        switch (id) {
            case "pool":
                return { id, text: FILE_POOL_NOTE };
            case "emlst":
                return { id, text: EMAIL_STORAGE_RULE, link: { label: "More on email storage", href: PLAN_DOCS.emailStorage } };
            case "egres": {
                // Whether Premium bandwidth is billed is its own rate line on the
                // card, so this note only says what is metered and what is refused.
                const parts = ["Bandwidth is metered per calendar month (UTC)."];
                if (!planSwitches.enforce_egress_hard_stop) {
                    parts.push("The allowance is not enforced yet: requests past it are still served.");
                } else if (stopping.length) {
                    parts.push(`On ${labels(stopping)}, downloads and hosted pages past the allowance are refused until the month resets.`);
                    if (billing.length) parts.push(`${labels(billing)} is never refused for bandwidth.`);
                }
                return { id, text: parts.join(" "), link: { label: "More on bandwidth", href: PLAN_DOCS.bandwidth } };
            }
            case "emlsd": {
                const parts = [SEND_COUNTING_RULE];
                if (plans.some((p) => p.key === FREE_PLAN)) parts.push(FREE_OWNER_CAP_RULE);
                parts.push(COMPLAINT_SHUTOFF_NOTE);
                return { id, text: parts.join(" "), link: { label: "More on sending limits", href: PLAN_DOCS.sending } };
            }
            case "stop": {
                if (!stopping.length) return null;
                // Free and the perpetual licences carry no subscription, so a
                // suspended project on them can be deleted; a subscribed one never is.
                const deletable = stopping.filter((p) => p.spec.deletable);
                let text = `On ${labels(stopping)}, writes, signups, sends and new subscribers past a limit are refused instead of billed. `
                    + "A project over its database or file storage limit is suspended until data is reduced or the plan is upgraded";
                text += deletable.length
                    ? `, and a ${labels(deletable)} project still suspended after 30 days can be permanently deleted.`
                    : ".";
                return { id, text, link: { label: "More on the hard stop", href: PLAN_DOCS.hardStop } };
            }
        }
        return null;
    };

    const notes: PlanNote[] = [];
    for (const id of PLAN_NOTE_ORDER) {
        if (!used.has(id)) continue;
        const note = build(id);
        if (note) notes.push(note);
    }
    return notes;
}

/* ── Plan & Usage meter lines ───────────────────────────────────────────────
 * At most one short line under a meter, plus a link to the docs section that
 * has the whole rule. A state the owner can act on replaces the rule line. */

export type MeterNote = { text: string; danger: boolean; href: string };

/** Under the Email sends meter: a block in force, else how a send is counted. */
export function emailSendsMeterNote(svc: ServiceLike | null | undefined): MeterNote {
    const row = svc?.service;
    const planKey = svc ? planKeyOf(svc) : "";
    if (row) {
        const block = newsletterSendBlock(row, { monthlySendLimit: Number(modelSpecs[planKey]?.email_sends) || null });
        if (block.kind === "complaints") {
            return { text: "Newsletter sending is stopped over spam complaints.", danger: true, href: PLAN_DOCS.complaints };
        }
        // Only a hard stop plan refuses past its sends; Premium bills them.
        if (block.kind === "plan_sends" && hardStopOf(planKey)) {
            return { text: "All sends used: newsletters are refused until next month (UTC).", danger: true, href: PLAN_DOCS.sending };
        }
    }
    return { text: "Counted per recipient.", danger: false, href: PLAN_DOCS.sending };
}

/** Under the Email Storage meter: what happens when it is full. */
export function emailStorageMeterNote(svc: ServiceLike | null | undefined): MeterNote {
    const planKey = svc ? planKeyOf(svc) : "";
    // A region still serving a limit of 0 suspends above a small tolerance.
    if (svc?.formattedSpecs?.serviceStatus?.storage?.email_limit_zero) {
        return { text: `None included: above ${EMAIL_SUSPEND_TEXT} the project is suspended.`, danger: false, href: PLAN_DOCS.emailStorage };
    }
    return hardStopOf(planKey)
        ? { text: "When full, the oldest newsletters are deleted.", danger: false, href: PLAN_DOCS.emailStorage }
        : { text: "Past the limit, the excess is billed.", danger: false, href: PLAN_DOCS.emailStorage };
}

export type NewsletterBlockCopy = { id: "complaints" | "plan_sends"; icon: string; text: string };

/**
 * What the newsletter page says when this project cannot send right now, or null
 * when nothing KNOWN blocks it. Both states are read off the served row, so a
 * region that does not publish one of them yet simply shows nothing here instead
 * of a block the owner cannot explain.
 *
 * The per-owner cap has no banner: nothing joins that counter onto the service
 * row, so the page cannot know. An owner who hits it learns from the reply the
 * backend sends to their newsletter address, and the rule is on the page as a
 * rule either way.
 */
export function newsletterBlockCopy(svc: ServiceLike | null | undefined): NewsletterBlockCopy | null {
    const row = svc?.service;
    if (!svc || !row) return null;
    const planKey = planKeyOf(svc);
    const block = newsletterSendBlock(row, {
        monthlySendLimit: Number(modelSpecs[planKey]?.email_sends) || null,
    });
    switch (block.kind) {
        case "complaints":
            return {
                id: block.kind,
                icon: "block",
                text: `Newsletter sending is stopped on this project because of spam complaints. ${COMPLAINT_SHUTOFF_RULE} `
                    + `Contact ${SUPPORT_EMAIL} to have it looked at.`,
            };
        case "plan_sends":
            return {
                id: block.kind,
                icon: "block",
                text: `This project has used all ${groupCount(block.limit)} of its ${planLabelOf(planKey)} sends this month, `
                    + `so newsletters are refused until the counter resets on the first of next month (UTC). ${SEND_COUNTING_RULE}`,
            };
    }
    return null;
}

const planKeyOf = (svc: ServiceLike): string => planKeyForGroup(svc?.service?.group);
const planLabelOf = (planKey: string): string => modelSpecs[planKey]?.label || planKey;
const hardStopOf = (planKey: string): boolean => modelSpecs[planKey]?.hard_stop !== false;
const canChangePlanOf = (svc: ServiceLike): boolean => !!svc?.formattedSpecs?.serviceStatus?.canChangePlan;
const subscriptionPath = (svc: ServiceLike): string => `/subscription/${svc.id}`;

/** Keys this project's own plan would keep it suspended over (strict, as the
 *  backend resumes). null while the limits are unknown. */
function ownSuspendingKeys(svc: ServiceLike): StoredLimitKey[] | null {
    const planKey = planKeyOf(svc);
    return suspendingLimits(svc?.service?.storage_info, storedLimitsForPlan(planKey), hardStopOf(planKey));
}

/** Keys that would suspend this project on another plan (a plan change is
 *  strict). null while the limits are unknown. */
function keysOnPlan(svc: ServiceLike, planKey: string): StoredLimitKey[] | null {
    return suspendingLimits(svc?.service?.storage_info, storedLimitsForPlan(planKey), true);
}

/** One row per key: "Database storage: 612.00MB of 500MB", with where to
 *  delete it. */
export function limitRows(svc: ServiceLike, keys: StoredLimitKey[] | null, planKey: string): LimitRow[] {
    if (!keys?.length) return [];
    const usage = storedUsage(svc?.service?.storage_info);
    const planLabel = planLabelOf(planKey);
    return keys.map((key) => {
        const used = convertBytesToReadableStorage(usage[key]);
        const limit = limitText(planKey, key);
        const text = limit === "0B"
            ? `${STORED_LIMIT_LABEL[key]}: ${used}, none included on ${planLabel}`
            : `${STORED_LIMIT_LABEL[key]}: ${used} of ${limit}`;
        return { key, text, ...dataLink(svc, key) };
    });
}

const RESUME_PHRASE: Record<ResumeKind, string> = {
    subscribe: "subscribe to a plan that holds its data",
    upgrade: "upgrade to Premium",
    support: `contact ${SUPPORT_EMAIL}`,
};

function resumeOf(svc: ServiceLike): ResumeKind {
    const planKey = planKeyOf(svc);
    return resumeKind(hardStopOf(planKey), hasSubscriptionId(svc?.service?.subs_id), canChangePlanOf(svc));
}

/** The deletion sentence of a suspended project. `cleanup` adds "delete data" as
 *  a way out, which only helps when something is known to be over. */
function deletionSentence(clock: SuspensionClock | null, resume: ResumeKind, cleanup: boolean): string {
    const phrase = RESUME_PHRASE[resume];
    const way = cleanup ? `delete data or ${phrase}` : phrase;
    // Nothing is ever deleted without a final notice: the sweep refuses to delete
    // until the notice for the current clock is at least 7 days old.
    const warned = " We e-mail you again before that.";
    switch (clock?.kind) {
        case "held":
            return "It is not deleted while its subscription is active.";
        case "scheduled":
            return `It can be permanently deleted as early as ${dateFormat(clock.at as number)} unless you ${way}.` + warned;
        case "due":
            return `It is due for permanent deletion. To keep it, ${way} now.`;
        default:
            return `It can be permanently deleted 30 days after it was suspended unless you ${way}.` + warned;
    }
}

function resumeActions(svc: ServiceLike, resume: ResumeKind): BannerAction[] {
    if (resume === "subscribe") return [{ label: "Subscribe", to: subscriptionPath(svc) }];
    if (resume === "upgrade") return [{ label: "Upgrade", to: subscriptionPath(svc) }];
    return [];
}

/**
 * Every banner a project page shows, in order. State banners are exclusive; a
 * running project adds the over-limit or near-limit warning and the bandwidth
 * banner after any payment or cancellation banner.
 */
export function projectBanners(svc: ServiceLike | null | undefined, now: number = Date.now()): ProjectBanner[] {
    const row = svc?.service;
    if (!svc || !row) return [];

    const planKey = planKeyOf(svc);
    const planLabel = planLabelOf(planKey);
    const hardStop = hardStopOf(planKey);
    const ownLimits = storedLimitsForPlan(planKey);
    const ownKeys = suspendingLimits(row.storage_info, ownLimits, hardStop);
    const nearKeys = nearStoredLimits(row.storage_info, suspensionLimits(ownLimits, hardStop));
    const sub = svc.subscription;
    const canChangePlan = canChangePlanOf(svc);
    const upgradeAction: BannerAction[] = canChangePlan ? [{ label: "Upgrade plan", to: subscriptionPath(svc) }] : [];

    const state = projectState(row.active, sub, !!ownKeys?.length);
    const ids = bannerIds(state, {
        over: !!ownKeys?.length,
        near: !!nearKeys?.length,
        bandwidth: bandwidthBlocked(row.egrblk, planSwitches.enforce_egress_hard_stop, hardStop, now),
    });

    const build = (id: BannerId): Omit<ProjectBanner, "id"> | null => {
        switch (id) {
            case "contained":
                return {
                    tone: "danger", icon: "priority_high", rows: [], after: "", actions: [],
                    text: "This project is contained. Only super admins can see it, and it is never deleted automatically.",
                };
            case "deleted":
            case "unknown":
                return {
                    tone: "danger", icon: "priority_high", rows: [], after: "", actions: [],
                    text: `This project is ${serviceStatusLabel(row.active).toLowerCase()}. Its users cannot use it and its settings cannot be changed. Contact ${SUPPORT_EMAIL} if you think this is a mistake.`,
                };
            case "disabled":
                return disabledBanner(svc);
            case "suspended": {
                const { kind, keys } = suspendedKind(row, ownKeys);
                const resume = resumeOf(svc);
                const clock = suspensionClock(row, now);
                const actions: BannerAction[] = [...resumeActions(svc, resume)];
                let text: string;
                if (kind === "over") {
                    text = `This project is suspended because it holds more than the ${planLabel} plan allows. Its users cannot use it until it is back under the limits.`;
                } else if (kind === "back_under") {
                    text = `This project is suspended, but its stored data is now under the ${planLabel} limits. It returns to normal automatically once the change is processed.`;
                } else if (kind === "checking") {
                    text = `This project is suspended because its stored data went over the ${planLabel} plan limits. Checking which limits are still over.`;
                } else {
                    text = "This project is suspended. Its users cannot use it.";
                }
                // Owner access while suspended is the one thing that is easy to get wrong:
                // the project page, the data pages and delete still work, nothing else does.
                text += " You can still open the project in the dashboard and delete its data; other settings are locked until it is back to normal.";
                if (kind !== "generic") actions.push({ label: "Check again", run: "refresh" });
                // A payment problem Stripe has not given up on yet: the owner can fix it here.
                if (["past_due", "unpaid", "incomplete"].includes(sub?.status)) {
                    actions.push({ label: "Update payment method", run: "billing" });
                }
                return {
                    tone: "danger", icon: "priority_high",
                    text,
                    rows: limitRows(svc, keys, planKey),
                    after: deletionSentence(clock, resume, kind !== "generic"),
                    actions,
                };
            }
            case "past_due": {
                const freeKeys = keysOnPlan(svc, FREE_PLAN);
                return {
                    tone: "danger", icon: "priority_high",
                    text: `The last payment failed. The ${planLabel} plan stays active while Stripe retries the charge.`,
                    rows: limitRows(svc, freeKeys, FREE_PLAN),
                    after: freeKeys?.length
                        ? "If every retry fails, this project moves to the Free plan and is suspended, because it holds more than Free allows."
                        : "If every retry fails, this project moves to the Free plan.",
                    actions: [{ label: "Update payment method", run: "billing" }],
                };
            }
            case "cancel_scheduled": {
                const freeKeys = keysOnPlan(svc, FREE_PLAN);
                const endsOn = sub?.cancel_at ? dateFormat(sub.cancel_at * 1000) : "the end of the billing period";
                return {
                    tone: "warning", icon: "warning",
                    text: `The ${planLabel} subscription ends on ${endsOn}. This project then moves to the Free plan.`,
                    rows: limitRows(svc, freeKeys, FREE_PLAN),
                    after: freeKeys?.length
                        ? "It holds more than Free allows, so it will be suspended that day, and it can be permanently deleted as early as 30 days later unless you delete data first or resume the plan."
                        : "",
                    actions: [{ label: "Resume plan", to: subscriptionPath(svc) }],
                };
            }
            case "over_limit_running":
                return {
                    tone: "warning", icon: "warning",
                    text: `This project holds more than the ${planLabel} plan allows. Writes past a limit are refused, and a project that stays over can be suspended until data is deleted or the plan is upgraded.`,
                    rows: limitRows(svc, ownKeys, planKey),
                    after: "",
                    actions: upgradeAction,
                };
            case "near_limit":
                return {
                    tone: "warning", icon: "warning",
                    text: `This project is close to the ${planLabel} plan limits. Writes stop at a limit, and a project that holds more than its plan allows can be suspended until data is deleted or the plan is upgraded.`,
                    rows: limitRows(svc, nearKeys, planKey),
                    after: "",
                    actions: upgradeAction,
                };
            case "bandwidth": {
                const allowance = svc.formattedSpecs?.currentPlan?.egress || "allowance";
                const resetsOn = new Date(firstOfNextUtcMonth(now)).toISOString().slice(0, 10);
                return {
                    tone: "warning", icon: "block",
                    text: `This month's bandwidth allowance of ${allowance} is used up. Downloads and hosted pages are refused until ${resetsOn} (UTC).`,
                    rows: [], after: "",
                    actions: upgradeAction,
                };
            }
        }
        return null;
    };

    const banners: ProjectBanner[] = [];
    for (const id of ids) {
        const banner = build(id);
        if (banner) banners.push({ id, ...banner });
    }
    return banners;
}

/** The project list's state cell: its text and its dot class. */
export function projectStateCell(svc: ServiceLike | null | undefined, now: number = Date.now()): { text: string; cls: string } {
    const row = svc?.service;
    if (!svc || !row) return { text: "Unknown", cls: "bq-state--disabled" };
    const sub = svc.subscription;
    const ownKeys = ownSuspendingKeys(svc);
    const state = projectState(row.active, sub, !!ownKeys?.length);
    const freeKeys = state === "cancel_scheduled" ? keysOnPlan(svc, FREE_PLAN) : null;
    const cell = stateCell(state, suspensionClock(row, now), sub?.cancel_at ? sub.cancel_at * 1000 : null, freeKeys);
    const date = cell.at ? dateFormat(cell.at) : "";
    switch (cell.kind) {
        case "running":
            return { text: "Running", cls: "bq-state--running" };
        case "disabled":
            return { text: "Disabled", cls: "bq-state--disabled" };
        case "contained":
            return { text: "Contained", cls: "bq-state--disabled" };
        case "deleted":
            return { text: "Deleted", cls: "bq-state--disabled" };
        case "over_limit":
            return { text: "Over limit", cls: "bq-state--cancel" };
        case "payment_failed":
            return { text: "Payment failed", cls: "bq-state--cancel" };
        case "deletes":
            return { text: `Deletes as early as ${date}`, cls: "bq-state--suspended" };
        case "deleting":
            return { text: "Due for deletion", cls: "bq-state--suspended" };
        case "suspended":
            return { text: "Suspended", cls: "bq-state--suspended" };
        case "suspends":
            return { text: date ? `Suspends ${date}` : "Suspends at period end", cls: "bq-state--cancel" };
        case "free_from":
            return { text: date ? `Free from ${date}` : "Moves to Free", cls: "bq-state--cancel" };
        case "cancels":
            return { text: date ? `Cancels ${date}` : "Cancelling", cls: "bq-state--cancel" };
    }
    return { text: "Unknown", cls: "bq-state--disabled" };
}

export type CancelDialogCopy = {
    kind: "already_suspended" | "checking" | "suspends" | "fits";
    keep: string;
    rows: LimitRow[];
    consequence: string;
    note: string;
    /** false while the Free limits are unknown: [Cancel Plan] waits for them. */
    canCancel: boolean;
};

/** The cancel dialog on the subscription page. */
export function cancelDialogCopy(svc: ServiceLike | null | undefined, now: number = Date.now()): CancelDialogCopy {
    const row = svc?.service;
    const sub = svc?.subscription;
    const planLabel = svc ? planLabelOf(planKeyOf(svc)) : "current";
    const freeKeys = svc ? keysOnPlan(svc, FREE_PLAN) : null;
    const kind = cancelConsequence(row?.active, freeKeys);
    const periodEnd = sub?.current_period_end
        ? dateFormat(sub.current_period_end * 1000)
        : "the end of the current billing period";

    if (kind === "already_suspended") {
        const clock = suspensionClock(row, now);
        let consequence: string;
        if (clock?.kind === "held") {
            consequence = "It is not deleted while its subscription is active. Once the subscription ends it moves to the Free plan, and it can be permanently deleted as early as 30 days later unless its data fits the Free limits.";
        } else if (clock?.kind === "scheduled") {
            consequence = `It can be permanently deleted as early as ${dateFormat(clock.at as number)} unless you delete data first. Cancelling does not change that date.`;
        } else if (clock?.kind === "due") {
            consequence = "It is due for permanent deletion unless you delete data first. Cancelling does not change that.";
        } else {
            consequence = "It can be permanently deleted 30 days after it was suspended unless you delete data first. Cancelling does not change that date.";
        }
        return { kind, keep: "This project is already suspended.", rows: [], consequence, note: "", canCancel: true };
    }

    const keep = `This project keeps the ${planLabel} plan until ${periodEnd}. Then it moves to the Free plan, where stored data must fit the Free limits.`;

    // Owner decision 11: Free includes email storage, so sent newsletters move
    // with the project and the oldest are deleted once that storage is full;
    // email storage no longer suspends. A region still serving a Free email
    // limit of 0 keeps the older warning, where a small amount of stored
    // newsletters is a warning and anything above the tolerance suspends.
    const email = storedUsage(row?.storage_info).emlst;
    const freeEmailText = limitText(FREE_PLAN, "emlst"); // "" until GET /plan answers
    let note = "";
    if (email > 0 && freeEmailText === "0B") {
        if (email <= QUOTA_SUSPEND_MIN_BYTES) {
            note = `It stores ${convertBytesToReadableStorage(email)} of sent newsletters. Free includes no email storage, and above ${EMAIL_SUSPEND_TEXT} the project is suspended, so delete old newsletters before then.`;
        }
    } else if (email > 0 && freeEmailText) {
        note = `It stores ${convertBytesToReadableStorage(email)} of sent newsletters. Free includes ${freeEmailText} of email storage, and once that is full the oldest newsletters are deleted to make room.`;
    }

    if (kind === "checking") {
        return { kind, keep, rows: [], consequence: "Checking this project's usage against the Free limits.", note, canCancel: false };
    }
    if (kind === "suspends") {
        return {
            kind, keep,
            rows: limitRows(svc as ServiceLike, freeKeys, FREE_PLAN),
            consequence: "It holds more than the Free plan allows, so on that day it will be suspended: its users cannot use it, and it can be permanently deleted as early as 30 days later unless you delete data or subscribe again.",
            note,
            canCancel: true,
        };
    }
    return {
        kind, keep, rows: [],
        consequence: "If its stored data goes over a Free limit, it can be suspended, and permanently deleted as early as 30 days later unless you delete data or subscribe again.",
        note,
        canCancel: true,
    };
}

export type DowngradeCopy = { kind: "checking" | "suspends" | "fits"; rows: LimitRow[]; text: string };

/** Whether moving this project to Standard would suspend it (a plan change is
 *  strict), with the rows that are over. */
export function downgradeCopy(svc: ServiceLike | null | undefined): DowngradeCopy {
    const keys = svc ? keysOnPlan(svc, STANDARD_PLAN) : null;
    if (keys === null) return { kind: "checking", rows: [], text: "Checking this project's usage against the Standard limits." };
    if (!keys.length) return { kind: "fits", rows: [], text: "" };
    return {
        kind: "suspends",
        rows: limitRows(svc as ServiceLike, keys, STANDARD_PLAN),
        text: "Downgrading suspends it right away: its users cannot use it until data is deleted or the plan is upgraded again.",
    };
}

/** Rows over the limits for a suspended project (service page "Over the limit"). */
export function suspendedLimitRows(svc: ServiceLike | null | undefined): LimitRow[] {
    const row = svc?.service;
    if (!svc || row?.active !== -1) return [];
    const { kind, keys } = suspendedKind(row, ownSuspendingKeys(svc));
    return kind === "over" ? limitRows(svc, keys, planKeyOf(svc)) : [];
}

/** The subscription page header of a suspended project: two lines. */
export function suspendedHeader(svc: ServiceLike | null | undefined, now: number = Date.now()): { title: string; detail: string } {
    const row = svc?.service;
    if (!svc || row?.active !== -1) return { title: "", detail: "" };
    const planLabel = planLabelOf(planKeyOf(svc));
    const { kind } = suspendedKind(row, ownSuspendingKeys(svc));
    // "Delete data" is only a way out when something is known to be over.
    const cleanup = kind !== "generic";
    const title = kind === "over"
        ? `"${row.name}" is suspended because it holds more than the ${planLabel} plan allows.`
        : `"${row.name}" is suspended.`;
    const resume = resumeOf(svc);
    const below = resume === "upgrade" ? "upgrade below" : resume === "subscribe" ? "subscribe below" : `contact ${SUPPORT_EMAIL}`;
    const way = cleanup ? `delete data or ${below}` : below;
    const clock = suspensionClock(row, now);
    let detail: string;
    switch (clock?.kind) {
        case "held":
            detail = "It is not deleted while its subscription is active.";
            break;
        case "scheduled":
            detail = `It can be permanently deleted as early as ${dateFormat(clock.at as number)} unless you ${way}.`;
            break;
        case "due":
            detail = `It is due for permanent deletion. To keep it, ${way}.`;
            break;
        default:
            detail = `It can be permanently deleted 30 days after it was suspended unless you ${way}.`;
    }
    return { title, detail };
}

/** The Plan & Usage card's line for a hard-stop plan: one sentence, with a
 *  link to PLAN_DOCS.hardStop for the refusals, the suspension and the 30-day
 *  deletion. A project that is actually over a limit gets its own banner and
 *  the "Over the limit" rows, so this line only states the rule. */
export function hardStopNote(): string {
    return "Hard stop: writes stop at the plan limits, and a project over its database or file storage limit is suspended.";
}

/** The note under the bandwidth meter, while a switch is still off. */
export function bandwidthMeterNote(hardStop: boolean | undefined): string {
    if (hardStop !== false && !planSwitches.enforce_egress_hard_stop) return "Allowance not enforced yet.";
    if (hardStop === false && !planSwitches.bill_egress_overage) return "Overage not billed yet.";
    return "";
}

/** The Plan button on the service page: resume a cancelling plan, subscribe a
 *  suspended Free project, otherwise upgrade. */
export function planCtaLabel(svc: ServiceLike | null | undefined): string {
    if (svc?.subscription?.will_cancel) return "Resume Plan";
    if (svc?.service?.active === -1 && svc?.service?.group === 1) return "Subscribe";
    return "Upgrade Plan";
}
